import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  HrailRailCurve,
  HrailRailCurvesListFilters,
  InsertHrailRailCurveBody,
  UpdateHrailRailCurveBody,
} from "./types/railCurves";

export const useHrailRailCurvesApi = () => {
  const api = useApi();

  const getAdminHrailRailCurve = useCallback(
    (railCurveId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/rail_curves/${railCurveId}`;
      return useSWRMutation<HrailRailCurve, CustomError, string>(url, (url) =>
        api.get<HrailRailCurve>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailRailCurves = useCallback(
    (query: PaginationQuery & HrailRailCurvesListFilters, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/rail_curves`, query);
      return useSWRImmutable<Paginated<HrailRailCurve>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailRailCurve>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailRailCurves = useCallback(
    (query: PaginationQuery & HrailRailCurvesListFilters, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/rail_curves`, query);
      return api.get<Paginated<HrailRailCurve>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailRailCurve = useCallback(
    async (body: InsertHrailRailCurveBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailRailCurveBody>(
        `/admin/settings/hrail/rail_curves`,
        body,
      );
      mutateCache([`/admin/settings/hrail/rail_curves`, `/settings/hrail`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailRailCurve = useCallback(
    async (railCurveId: string, body: UpdateHrailRailCurveBody): Promise<void> => {
      const url = `/admin/settings/hrail/rail_curves/${railCurveId}`;
      await api.put<UpdateHrailRailCurveBody>(url, body);
      mutateCache([`/admin/settings/hrail/rail_curves`, `/settings/hrail`], url);
    },
    [api],
  );

  const updateAdminHrailRailCurveVisibility = useCallback(
    async (railCurveId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/rail_curves/${railCurveId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/rail_curves`, `/settings/hrail`]);
    },
    [api],
  );

  const updateAdminHrailRailCurveOrder = useCallback(
    async (railCurveId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/rail_curves/${railCurveId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/rail_curves`, `/settings/hrail`]);
    },
    [api],
  );

  const deleteAdminHrailRailCurve = useCallback(
    async (railCurveId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/rail_curves/${railCurveId}`);
      mutateCache([`/admin/settings/hrail/rail_curves`, `/settings/hrail`]);
    },
    [api],
  );

  return {
    getAdminHrailRailCurve,
    useAdminHrailRailCurves,
    getAdminHrailRailCurves,
    insertAdminHrailRailCurve,
    updateAdminHrailRailCurve,
    updateAdminHrailRailCurveVisibility,
    updateAdminHrailRailCurveOrder,
    deleteAdminHrailRailCurve,
  };
};
